import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';

import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

import Dot from 'components/@extended/Dot';
import { activeItem, openDrawer } from 'slices/menu';
import { LAYOUT_CONST } from 'config';

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const itemRef = useRef(null);

  const { menuOrientation } = useSelector(({ config }) => config);
  const { drawerOpen, openItem } = useSelector((state) => state.menu);
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = {
    component: forwardRef((props, ref) => <Link {...props} to={item.url} target={itemTarget} ref={ref} />),
    ref: itemRef
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget, ref: itemRef };
  }

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;

  const isSelected = openItem.findIndex((id) => id === item.id) > -1;

  const { pathname } = useLocation();

  useEffect(() => {
    const shouldActivate = pathname.includes(item.url);

    if (shouldActivate) {
      dispatch(activeItem({ openItem: [item.id] }));
      if (isSelected && itemRef.current) {
        setTimeout(() => {
          const parentContainer = itemRef.current?.closest('[data-simplebar]');

          if (parentContainer) {
            const parentRect = parentContainer.getBoundingClientRect();
            const elementRect = itemRef.current.getBoundingClientRect();

            const isOutOfView = elementRect.top < parentRect.top || elementRect.bottom > parentRect.bottom;

            if (isOutOfView) {
              itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }, 100);
      }
    }
  }, [pathname, isSelected, item.id, item.url, dispatch]);

  const textColor = 'common.white';
  const iconSelectedColor = 'common.white';

  const verticalLayout = (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 28}px` : 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 1,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: 'primary.lighter'
          },
          '&.Mui-selected': {
            color: iconSelectedColor,
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: 'primary.lighter'
            }
          }
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: 'transparent'
            },
            bgcolor: 'transparent'
          }
        })
      }}
      onClick={() => downLG && dispatch(openDrawer(false))}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: 28,
            minHeight: 28,
            color: isSelected ? iconSelectedColor : textColor,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            ...(!drawerOpen
              ? {
                  width: 36,
                  height: 36,
                  '&:hover': {
                    bgcolor: 'common.white',
                    color: 'primary.main'
                  }
                }
              : {
                  marginRight: '12px'
                }),
            ...(isSelected && {
              background: theme.palette.common.white,
              bgcolor: 'common.white'
            })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography variant="h6" sx={{ color: textColor, ...(isSelected && { fontWeight: 600 }) }}>
              {item.title}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );

  const horizontalLayout = (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected': {
            bgcolor: 'transparent',
            color: iconSelectedColor,
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: 'transparent'
            }
          }
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: 'transparent'
            },
            bgcolor: 'transparent'
          }
        })
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: 36,
            ...(!drawerOpen && {
              borderRadius: 1.5,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'flex-start',
              '&:hover': {
                bgcolor: 'transparent'
              }
            }),
            ...(!drawerOpen &&
              isSelected && {
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'transparent'
                }
              })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}

      {!itemIcon && (
        <ListItemIcon
          sx={{
            color: isSelected ? 'primary.main' : 'secondary.main',
            ...(!drawerOpen && {
              borderRadius: 1.5,
              alignItems: 'center',
              justifyContent: 'flex-start',
              '&:hover': {
                bgcolor: 'transparent'
              }
            }),
            ...(!drawerOpen &&
              isSelected && {
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'transparent'
                }
              })
          }}
        >
          <Dot size={4} color={isSelected ? 'primary' : 'secondary'} />
        </ListItemIcon>
      )}
      <ListItemText
        primary={
          <Typography variant="h6" color="inherit">
            {item.title}
          </Typography>
        }
      />
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );

  return <>{menuOrientation === LAYOUT_CONST.VERTICAL_LAYOUT || downLG ? verticalLayout : horizontalLayout}</>;
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
